import { z } from 'zod';
import type { ToolReadingDto } from './gaps';
import { insightSchema } from './materials';
import {
  BMC_BLOCKS, BMC_ITEM_ID, BMC_LIST_BLOCKS, BMC_PREFIX,
  type Bmc, type BmcItem, type BmcListBlock,
} from './strategy';

/* ─────────────────────────────────────────────────────────────────────
   Business Model Canvas — itens, relações e as leituras do agente.

   Cada bloco (menos a proposta de valor) é uma lista de itens, e os itens
   se relacionam entre blocos: um canal com os segmentos que ele alcança,
   uma fonte de receita com quem paga, um custo com o recurso que ele
   sustenta. A relação vale nos dois sentidos e só entre blocos diferentes.
   ───────────────────────────────────────────────────────────────────── */

export const BMC_LABEL = Object.fromEntries(BMC_BLOCKS) as Record<(typeof BMC_BLOCKS)[number][0], string>;
/** Etapa 1: o centro do modelo. Etapa 2: o que o sustenta. */
export const BMC_CORE: BmcListBlock[] = ['segments', 'relationship'];
export const BMC_REST: BmcListBlock[] = ['channels', 'revenue', 'resources', 'activities', 'partners', 'costs'];

export const bmcText = (items: BmcItem[]) => items.map(i => i.text.trim()).filter(Boolean).join(', ');

/** Ids em todos os itens, relações só entre blocos diferentes e nos dois sentidos. */
export function withBmcNormalized(c: Bmc): Bmc {
  const used = new Set<string>();
  const ids = new Map<string, BmcListBlock>();
  const next = { ...c } as Bmc;
  for (const b of BMC_LIST_BLOCKS) {
    let n = 0;
    next[b] = c[b].map(i => {
      let id = i.id && BMC_ITEM_ID.test(i.id) && !used.has(i.id) ? i.id : '';
      while (!id || used.has(id)) id = `${BMC_PREFIX[b]}-${++n}`;
      used.add(id); ids.set(id, b);
      return { ...i, id };
    });
  }
  const edges = new Map<string, Set<string>>();
  const add = (a: string, z: string) => { if (!edges.has(a)) edges.set(a, new Set()); edges.get(a)!.add(z); };
  for (const b of BMC_LIST_BLOCKS) {
    for (const i of next[b]) {
      for (const l of i.links) {
        const other = ids.get(l);
        if (!other || other === b) continue;
        add(i.id!, l); add(l, i.id!);
      }
    }
  }
  for (const b of BMC_LIST_BLOCKS) next[b] = next[b].map(i => ({ ...i, links: [...(edges.get(i.id!) ?? [])].sort() }));
  return next;
}

/** Onde está cada item. */
export function bmcIndex(c: Bmc) {
  const m = new Map<string, { block: BmcListBlock; item: BmcItem }>();
  for (const b of BMC_LIST_BLOCKS) for (const i of c[b]) if (i.id) m.set(i.id, { block: b, item: i });
  return m;
}

/** Liga ou desliga a relação entre dois itens (dos dois lados). */
export function toggleBmcLink(c: Bmc, a: string, z: string, on: boolean): Bmc {
  const next = { ...c } as Bmc;
  for (const b of BMC_LIST_BLOCKS) {
    next[b] = c[b].map(i => {
      if (i.id !== a && i.id !== z) return i;
      const other = i.id === a ? z : a;
      const links = i.links.filter(l => l !== other);
      return { ...i, links: on ? [...links, other].sort() : links };
    });
  }
  return next;
}

/** Tira um item e as relações que apontavam para ele. */
export function removeBmcItem(c: Bmc, id: string): Bmc {
  const next = { ...c } as Bmc;
  for (const b of BMC_LIST_BLOCKS) next[b] = c[b].filter(i => i.id !== id).map(i => (i.links.includes(id) ? { ...i, links: i.links.filter(l => l !== id) } : i));
  return next;
}

/** Contas rápidas do canvas: blocos preenchidos, relações, segmentos sem canal. */
export function bmcMetrics(c: Bmc) {
  const idx = bmcIndex(c);
  const filled = BMC_LIST_BLOCKS.filter(b => c[b].some(i => i.text.trim())).length + (c.valueProp.trim() ? 1 : 0);
  let links = 0;
  for (const b of BMC_LIST_BLOCKS) for (const i of c[b]) links += i.links.length;
  const segs = c.segments.filter(s => s.text.trim());
  const reached = (s: BmcItem, block: BmcListBlock) => s.links.some(l => idx.get(l)?.block === block);
  return {
    filled, total: BMC_BLOCKS.length, relations: links / 2,
    segments: segs.length,
    withChannel: segs.filter(s => reached(s, 'channels')).length,
    withRelationship: segs.filter(s => reached(s, 'relationship')).length,
    withRevenue: segs.filter(s => reached(s, 'revenue')).length,
    ai: BMC_LIST_BLOCKS.reduce((n, b) => n + c[b].filter(i => i.ai).length, 0),
  };
}

/* ── as leituras do agente ────────────────────────────────────────── */

const pct = z.number().int().min(0).max(100);
export const bmcReadingSchema = z.object({
  /** consistência do modelo, 0–100, e uma frase sobre ela */
  consistencia: pct,
  resumo: z.string().trim().min(10).max(260),
  criterios: z.object({ clareza: pct, coerencia: pct, aderencia: pct, viabilidade: pct }),
  /** alinhamento do modelo ao propósito, 0–100 */
  alinhamento: pct,
  leituras: z.array(z.string().trim().min(4).max(200)).min(1).max(4),
  ajustes: z.array(z.string().trim().min(4).max(200)).max(4).default([]),
  insights: z.array(insightSchema).max(4).default([]),
});
export type BmcReading = z.infer<typeof bmcReadingSchema>;
export type BmcReadingDto = ToolReadingDto<BmcReading>;
export const BMC_CRITERIA = [
  ['clareza', 'Clareza da proposta'], ['coerencia', 'Coerência com o propósito'],
  ['aderencia', 'Aderência aos segmentos'], ['viabilidade', 'Viabilidade operacional'],
] as const;

/** O que a IA sugere: uma proposta de valor, ou itens para os blocos (com relações). */
export const bmcSuggestionSchema = z.object({
  proposta: z.string().trim().max(600).default(''),
  blocks: z.partialRecord(z.enum(BMC_LIST_BLOCKS as [BmcListBlock, ...BmcListBlock[]]), z.array(z.object({
    id: z.string().max(48),
    text: z.string().trim().min(2).max(200),
    links: z.array(z.string().max(48)).max(12).default([]),
  })).max(6)).default({}),
});
export type BmcSuggestion = z.infer<typeof bmcSuggestionSchema>;
export const bmcSuggestRequestSchema = z.object({
  mode: z.enum(['proposta', 'modulos']),
  /** os blocos a sugerir (modulos); vazio = os vazios da etapa 2 */
  blocks: z.array(z.enum(BMC_LIST_BLOCKS as [BmcListBlock, ...BmcListBlock[]])).max(8).default([]),
});
export interface BmcSuggestionDto extends BmcSuggestion { basic: boolean; note: string | null }
